import {
  CustomerAccountConfigurationKeyEnum,
  getCustomerDrawerOptions,
  getMarketAccountOptions,
  IconName,
  type ColumnDef,
  type Customer,
  type FilterableProperty,
  type InputsAndDropdownsDrawerOption,
  type MarketAccount,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { CustomerAccountConfigurationValueField } from './CustomerAccountConfigurationValueField';

export interface CustomerAccountConfiguration {
  ConfigID: string;
  Key: CustomerAccountConfigurationKeyEnum;
  Value: string;
  Counterparty?: string;
  MarketAccount?: string;
  Symbol?: string;
  UpdateUser?: string;
  UpdateAction?: string;
  Revision?: number;
}

export interface CustomerAccountConfigurationRow extends CustomerAccountConfiguration {
  Description: string;
  Type: string;
}

export type CustomerAccountConfigurationDrawerEntity = Partial<
  Omit<CustomerAccountConfiguration, 'UpdateUser' | 'UpdateAction' | 'Revision'>
>;

export const SUPPORTED_CUSTOMER_ACCOUNT_CONFIGURATION_KEYS: CustomerAccountConfigurationKeyEnum[] = [
  CustomerAccountConfigurationKeyEnum.EmailTemplateID,
  CustomerAccountConfigurationKeyEnum.EnablePassThroughMarketOrders,
  CustomerAccountConfigurationKeyEnum.FillRoundingOrder,
];

export const CUSTOMER_ACCOUNT_CONFIGURATION_KEY_FILTER = {
  key: 'Keys',
  field: 'Key',
  label: 'Key',
  icon: IconName.Key,
  options: SUPPORTED_CUSTOMER_ACCOUNT_CONFIGURATION_KEYS,
  getOptionLabel: identity,
} as const satisfies FilterableProperty;

export function isSupportedCustomerAccountConfigurationKey(
  key: string | undefined
): key is CustomerAccountConfigurationKeyEnum {
  return SUPPORTED_CUSTOMER_ACCOUNT_CONFIGURATION_KEYS.some(supportedKey => supportedKey === key);
}

function isBooleanConfigurationKey(key: string | undefined): boolean {
  return key === CustomerAccountConfigurationKeyEnum.EnablePassThroughMarketOrders;
}

function keySupportsCounterparty(key: string | undefined): boolean {
  return isSupportedCustomerAccountConfigurationKey(key);
}

function keySupportsMarketAccount(key: string | undefined): boolean {
  return (
    key === CustomerAccountConfigurationKeyEnum.EnablePassThroughMarketOrders ||
    key === CustomerAccountConfigurationKeyEnum.FillRoundingOrder
  );
}

function keySupportsSymbol(key: string | undefined): boolean {
  return key === CustomerAccountConfigurationKeyEnum.FillRoundingOrder;
}

function getKeyDescription(key: string | undefined): string {
  switch (key) {
    case CustomerAccountConfigurationKeyEnum.EmailTemplateID:
      return 'Associates a custom SendGrid email template with one customer, or with the organization when Customer is left empty.';
    case CustomerAccountConfigurationKeyEnum.EnablePassThroughMarketOrders:
      return 'When enabled, allows the customer to send market orders through to the market.';
    case CustomerAccountConfigurationKeyEnum.FillRoundingOrder:
      return 'Controls the rounding order applied to fill quantities.';
    default:
      return '';
  }
}

function getBooleanConfigurationValue(value: string | undefined): 'true' | 'false' {
  return value === 'true' || value === '1' ? 'true' : 'false';
}

export function getCustomerAccountConfigurationValueLabel(key: string | undefined, value: string | undefined): string {
  if (value == null || value === '') {
    return value ?? '';
  }
  if (isBooleanConfigurationKey(key)) {
    return getBooleanConfigurationValue(value) === 'true' ? 'True' : 'False';
  }
  return value;
}

export const CUSTOMER_ACCOUNT_CONFIGURATION_COLUMNS: ColumnDef<CustomerAccountConfigurationRow>[] = [
  {
    type: 'text',
    field: 'Key',
    sortable: true,
    initialSort: '+',
  },
  {
    type: 'text',
    field: 'Description',
  },
  {
    type: 'text',
    field: 'Value',
    params: {
      getLabel: (value, { data }) => getCustomerAccountConfigurationValueLabel(data?.Key, value),
    },
  },
  {
    type: 'text',
    field: 'Type',
    initialWidth: 100,
  },
  {
    type: 'customer',
    field: 'Counterparty',
    title: 'Customer',
  },
  {
    type: 'marketAccount',
    field: 'MarketAccount',
    title: 'Customer Account',
    params: {
      prefixCustomerMarketAccountsWithCpty: true,
    },
  },
  {
    type: 'text',
    field: 'Symbol',
  },
  {
    type: 'text',
    field: 'UpdateUser',
    initialHide: true,
  },
  {
    type: 'text',
    field: 'ConfigID',
    initialHide: true,
  },
  {
    type: 'text',
    field: 'Revision',
    initialHide: true,
  },
];

export function getCustomerAccountConfigurationDrawerOptions({
  customers,
  customerMarketAccountsList,
}: {
  customers: Customer[];
  customerMarketAccountsList: MarketAccount[];
}): InputsAndDropdownsDrawerOption<CustomerAccountConfigurationDrawerEntity>[] {
  return [
    {
      field: 'Key',
      type: 'dropdown',
      title: 'Key',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      guideText: form => getKeyDescription(form.Key),
      getDropdownOptions: () =>
        SUPPORTED_CUSTOMER_ACCOUNT_CONFIGURATION_KEYS.map(key => ({
          value: key,
          label: key,
          description: getKeyDescription(key),
        })),
      onChange: (newValue, onFormUpdate) => {
        const key = String(newValue);
        onFormUpdate('Value', isBooleanConfigurationKey(key) ? 'false' : '', true);
        if (!keySupportsCounterparty(key)) {
          onFormUpdate('Counterparty', '', true);
        }
        if (!keySupportsMarketAccount(key)) {
          onFormUpdate('MarketAccount', '', true);
        }
        if (!keySupportsSymbol(key)) {
          onFormUpdate('Symbol', '', true);
        }
      },
    },
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Customer',
      showClear: true,
      disabledWhenEditing: true,
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
      getIsHidden: form => !keySupportsCounterparty(form.Key),
      onChange: (_newValue, onFormUpdate) => onFormUpdate('MarketAccount', '', true),
    },
    {
      field: 'MarketAccount',
      type: 'dropdown',
      title: 'Customer Account',
      showClear: true,
      disabledWhenEditing: true,
      getIsHidden: form => !keySupportsMarketAccount(form.Key),
      getIsDisabled: form => !form.Counterparty,
      getDropdownOptions: form => {
        const filteredMarketAccounts = customerMarketAccountsList.filter(
          marketAccount => marketAccount.Counterparty === form.Counterparty
        );
        return getMarketAccountOptions(filteredMarketAccounts);
      },
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      title: 'Symbol',
      showClear: true,
      disabledWhenEditing: true,
      getIsHidden: form => !keySupportsSymbol(form.Key),
    },
    {
      field: 'ConfigID',
      type: 'input',
      title: 'Config ID',
      getIsDisabled: () => true,
    },
    {
      field: 'Value',
      type: 'custom',
      title: 'Value',
      getIsRequired: () => true,
      getIsHidden: form => !isSupportedCustomerAccountConfigurationKey(form.Key),
      renderer: CustomerAccountConfigurationValueField,
    },
  ];
}

export function toCustomerAccountConfigurationRow(entity: unknown): CustomerAccountConfigurationRow {
  const configuration = entity as CustomerAccountConfiguration;
  return {
    ...configuration,
    Description: getKeyDescription(configuration.Key),
    Type: isBooleanConfigurationKey(configuration.Key)
      ? 'Boolean'
      : isSupportedCustomerAccountConfigurationKey(configuration.Key)
        ? 'String'
        : '',
  };
}

export function getCustomerAccountConfigurationDrawerEntity(
  entity: CustomerAccountConfigurationRow
): CustomerAccountConfigurationDrawerEntity {
  return {
    ConfigID: entity.ConfigID,
    Key: entity.Key,
    Counterparty: entity.Counterparty,
    MarketAccount: entity.MarketAccount,
    Symbol: entity.Symbol,
    Value: isBooleanConfigurationKey(entity.Key) ? getBooleanConfigurationValue(entity.Value) : entity.Value,
  };
}

export function getCustomerAccountConfigurationDisplayName(
  entity: Pick<CustomerAccountConfigurationDrawerEntity, 'Key'>
): string {
  return entity.Key ?? '';
}

export function getCustomerAccountConfigurationEntityForSave(
  entity: CustomerAccountConfigurationDrawerEntity
): Record<string, string> {
  const value = isBooleanConfigurationKey(entity.Key)
    ? getBooleanConfigurationValue(entity.Value)
    : (entity.Value ?? '');
  if (entity.ConfigID) {
    return { Value: value };
  }

  const payload: Record<string, string> = {
    Key: entity.Key ?? '',
    Value: value,
  };
  if (entity.Counterparty) {
    payload.Counterparty = entity.Counterparty;
  }
  if (entity.MarketAccount) {
    payload.MarketAccount = entity.MarketAccount;
  }
  if (entity.Symbol) {
    payload.Symbol = entity.Symbol;
  }
  return payload;
}
